/**
 * Права из токена Mechta ID (claim `permissions`). Права настраиваются на роли
 * в IAM, и в токен приходит их объединение по всем ролям пользователя, поэтому
 * код проверяет только права и никогда — названия ролей.
 */
export enum PERMISSION {
  CATALOG_READ = 'catalog:read',
  ORDERS_READ = 'orders:read',
  ORDERS_WRITE = 'orders:write',
  PAYMENTS_WRITE = 'payments:write',
  ISSUE_WRITE = 'issue:write',
  ISSUE_ANY_PAYMENT = 'issue:any-payment',
  /** Выдача в подразделении, выбранном в профиле (складской грузчик). */
  ISSUE_IN_CHOSEN_SUBDIVISION = 'issue:in-chosen-subdivision',
  REFUNDS_WRITE = 'refunds:write',
  ORDER_CENTER_WRITE = 'order-center:write',
  EMPLOYEE_SELF_SERVICE = 'employee:self-service',
  /** Выбор магазина своего филиала (атрибут organisation_id). */
  SUBDIVISIONS_CHOOSE_BY_ORGANISATION = 'subdivisions:choose-by-organisation',
  /** Выбор магазина в городе своего подразделения. */
  SUBDIVISIONS_CHOOSE_BY_CITY = 'subdivisions:choose-by-city',
  /** Выбор любого подразделения компании. */
  SUBDIVISIONS_CHOOSE_ANY = 'subdivisions:choose-any',
  /** Вся работа — заказы, оплаты, выдача, возвраты — в выбранном подразделении (супер продавец). */
  SUBDIVISIONS_WRITE_IN_CHOSEN = 'subdivisions:write-in-chosen',
  PAYMENTS_KASPI_QR = 'payments:kaspi-qr',
  PAYMENTS_HALYK_QR = 'payments:halyk-qr',
  PAYMENTS_DIGITAL_SERVICES = 'payments:digital-services',
  ISSUE_REQUIRE_IMEI = 'issue:require-imei',
  AI_ASSISTANT_USE = 'ai-assistant:use',
  /** Вкладка «Новости» и сервис новостей. */
  NEWS_READ = 'news:read',
  /** «Сервисы → Trade-in». */
  TRADE_IN_READ = 'trade-in:read',
}

export function hasPermission(
  permissions: readonly string[],
  permission: PERMISSION,
): boolean {
  return permissions.includes(permission)
}

/** Хотя бы одно из прав; пустой список требований не ограничивает. */
export function hasAnyPermission(
  permissions: readonly string[],
  required: readonly PERMISSION[],
): boolean {
  return !required.length || required.some((p) => permissions.includes(p))
}

/**
 * Пункты «Сервисов»: каждый открывается своим правом, вкладка видна при любом
 * из них (сравнение товаров — всем, кто видит каталог).
 */
export const SERVICE_PERMISSIONS: readonly PERMISSION[] = [
  PERMISSION.CATALOG_READ,
  PERMISSION.TRADE_IN_READ,
  PERMISSION.REFUNDS_WRITE,
  PERMISSION.ORDER_CENTER_WRITE,
  PERMISSION.AI_ASSISTANT_USE,
]

/** Может смотреть чужие подразделения: выбирает их в профиле. */
export function canChooseSubdivision(permissions: readonly string[]): boolean {
  return hasAnyPermission(permissions, [
    PERMISSION.SUBDIVISIONS_CHOOSE_ANY,
    PERMISSION.SUBDIVISIONS_CHOOSE_BY_ORGANISATION,
    PERMISSION.SUBDIVISIONS_CHOOSE_BY_CITY,
  ])
}
